/**
 * The admin's edit and void, on an entry's detail (BR-08, BR-08a, D-020).
 *
 * Both need a reason, and both are visible to every partner afterwards: an
 * edited entry is stamped "amended" with its reason, and a voided one stays in
 * the list under its own filter with the reason beside it. Hiding the buttons
 * from partners is a courtesy, not the control — the server refuses an
 * amendment from anyone but the admin regardless (SEC-3).
 */

import { useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import {
  uploadReceipt,
  useAccounts,
  useAmendTransaction,
  useCategories,
  useVoidTransaction,
  type Category,
  type Transaction,
} from './api.js';

const MONEY_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * What a partner could have recorded by hand. Transfers, depreciation, loans
 * and qurbani advances belong to their own screens; the server refuses them
 * too (D-020), this only avoids offering a button that cannot work.
 */
const EDITABLE_TYPES = new Set([
  'expense',
  'income',
  'capital_in',
  'drawing',
  'payable_payment',
  'receivable_collection',
  'asset_purchase',
]);

/** Categories of the kind this entry can be filed under, children included. */
function categoryOptions(categories: Category[], type: string): Category[] {
  const kind = type === 'income' ? 'income' : 'expense';
  return categories
    .flatMap((parent) => [parent, ...(parent.children ?? [])])
    .filter((category) => category.kind === kind && category.isActive);
}

type Mode = 'idle' | 'edit' | 'void';

export function TransactionAdminActions({ txn }: { txn: Transaction }): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>('idle');

  if (user?.role !== 'admin' || txn.status === 'voided') return null;

  if (mode === 'edit') return <EditForm txn={txn} onDone={() => setMode('idle')} />;
  if (mode === 'void')
    return (
      <VoidForm
        txn={txn}
        onCancel={() => setMode('idle')}
        // The list refetches; with the default filter the entry leaves it and
        // the detail closes, and it is still there under "Voided".
        onDone={() => setMode('idle')}
      />
    );

  return (
    <div className="mt-4 flex gap-2">
      {EDITABLE_TYPES.has(txn.type) && (
        <button
          type="button"
          onClick={() => setMode('edit')}
          className="min-h-touch flex-1 rounded-lg bg-amber-100 px-3 py-2 font-medium text-amber-900"
        >
          {t('finance.edit.open')}
        </button>
      )}
      <button
        type="button"
        onClick={() => setMode('void')}
        className="min-h-touch flex-1 rounded-lg bg-red-50 px-3 py-2 font-medium text-red-800"
      >
        {t('finance.void.open')}
      </button>
    </div>
  );
}

function EditForm({ txn, onDone }: { txn: Transaction; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const amend = useAmendTransaction();
  const { data: categories = [] } = useCategories();
  const { data: accounts = [] } = useAccounts();

  const [amount, setAmount] = useState(txn.amount);
  const [txnDate, setTxnDate] = useState(txn.txnDate);
  const [categoryId, setCategoryId] = useState<number | null>(txn.categoryId);
  const [accountId, setAccountId] = useState<number | null>(txn.accountId);
  const [note, setNote] = useState(txn.note ?? '');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Only what actually moved, so the audit row's before/after is about the
  // change rather than about every field being rewritten.
  const changes = {
    ...(amount !== txn.amount ? { amount } : {}),
    ...(txnDate !== txn.txnDate ? { txnDate } : {}),
    ...(categoryId !== null && categoryId !== txn.categoryId ? { categoryId } : {}),
    ...(accountId !== null && accountId !== txn.accountId ? { accountId } : {}),
    ...(note !== (txn.note ?? '') ? { note } : {}),
  };
  const changed = Object.keys(changes).length > 0 || receipt !== null;
  const canSubmit = MONEY_DRAFT.test(amount) && reason.trim().length >= 3 && changed && !busy;

  const categoryChoices = categoryOptions(categories, txn.type);
  // A retired account stays selectable only if the entry already uses it.
  const accountChoices = accounts.filter((account) => account.isActive || account.id === txn.accountId);

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});
    setBusy(true);

    try {
      const receiptUrl = receipt !== null ? await uploadReceipt(receipt) : undefined;
      await amend.mutateAsync({
        id: txn.id,
        ...changes,
        ...(receiptUrl !== undefined ? { receiptUrl } : {}),
        reason: reason.trim(),
      });
      onDone();
    } catch (error) {
      const key = errorMessageKey(error);
      // Only the admin sees this form, so a refusal here is a rule about the
      // entry (linked, transfer, automatic), not a missing permission.
      setErrorKey(key === 'error.FORBIDDEN' ? 'finance.edit.notEditable' : key);
      setFieldErrors(errorFields(error) ?? {});
    } finally {
      setBusy(false);
    }
  }

  const fieldError = (name: string): JSX.Element | null =>
    fieldErrors[name] !== undefined ? <p className="mt-1 text-sm text-red-700">{fieldErrors[name]}</p> : null;

  const label = 'mb-1 mt-3 block text-xs font-medium text-gray-700';
  const input = 'min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2';

  return (
    <form
      className="mt-4 rounded-lg bg-amber-50 p-3"
      aria-label={t('finance.edit.title')}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <label className={label} htmlFor="edit-amount">
        {t('finance.edit.amount')}
      </label>
      <input
        id="edit-amount"
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        className={`${input} tabular-nums`}
      />
      {/* What is being replaced, in front of the person replacing it. */}
      <p className="mt-1 text-xs text-gray-600">{t('partnerLedger.was', { amount: formatMoney(txn.amount) })}</p>
      {fieldError('amount')}

      <label className={label} htmlFor="edit-date">
        {t('finance.edit.date')}
      </label>
      <input
        id="edit-date"
        type="date"
        value={txnDate}
        onChange={(event) => setTxnDate(event.target.value)}
        className={input}
      />
      {fieldError('txnDate')}

      {txn.categoryId !== null && (
        <>
          <label className={label} htmlFor="edit-category">
            {t('finance.edit.category')}
          </label>
          <select
            id="edit-category"
            value={categoryId ?? ''}
            onChange={(event) => setCategoryId(Number(event.target.value))}
            className={input}
          >
            {categoryChoices.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {fieldError('categoryId')}
        </>
      )}

      {txn.accountId !== null && (
        <>
          <label className={label} htmlFor="edit-account">
            {t('finance.edit.account')}
          </label>
          <select
            id="edit-account"
            value={accountId ?? ''}
            onChange={(event) => setAccountId(Number(event.target.value))}
            className={input}
          >
            {accountChoices.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          {fieldError('accountId')}
        </>
      )}

      <label className={label} htmlFor="edit-note">
        {t('finance.edit.note')}
      </label>
      <input
        id="edit-note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={1000}
        className={input}
      />
      {fieldError('note')}

      <label className={label} htmlFor="edit-receipt">
        {t(txn.receiptUrl !== null ? 'finance.edit.replaceReceipt' : 'finance.edit.addReceipt')}
      </label>
      <input
        id="edit-receipt"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={(event) => setReceipt(event.target.files?.[0] ?? null)}
        className="w-full text-sm"
      />
      {fieldError('receiptUrl')}

      <label className={label} htmlFor="edit-reason">
        {t('finance.edit.reason')}
      </label>
      <input
        id="edit-reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        maxLength={500}
        className={input}
      />
      <p className="mt-1 text-xs text-gray-600">{t('partnerLedger.reasonHint')}</p>
      {fieldError('reason')}

      {errorKey !== null && (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(busy ? 'partnerLedger.saving' : 'partnerLedger.save')}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-touch rounded-lg bg-white px-3 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

function VoidForm({
  txn,
  onCancel,
  onDone,
}: {
  txn: Transaction;
  onCancel: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const voidTxn = useVoidTransaction();
  const [reason, setReason] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const canSubmit = reason.trim().length >= 3 && !voidTxn.isPending;

  async function submit(): Promise<void> {
    setErrorKey(null);
    try {
      await voidTxn.mutateAsync({ id: txn.id, reason: reason.trim() });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form
      className="mt-4 rounded-lg bg-red-50 p-3"
      aria-label={t('finance.void.title')}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      {/* BR-08 — nothing is deleted; say so, so nobody fears losing the record. */}
      <p className="text-sm text-red-900">{t('finance.void.explain')}</p>

      <label className="mb-1 mt-3 block text-xs font-medium text-gray-700" htmlFor="void-reason">
        {t('finance.void.reason')}
      </label>
      <input
        id="void-reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        maxLength={500}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
      />

      {errorKey !== null && (
        <p role="alert" className="mt-3 rounded-lg bg-white px-3 py-2 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-red-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(voidTxn.isPending ? 'finance.void.voiding' : 'finance.void.confirm')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-touch rounded-lg bg-white px-3 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}
